import React, { useRef, useEffect, useCallback } from 'react';
import './FeaturesPage.css';
import { trackFeaturesPageVisit } from './userJourneyTracker';

const SPEED = 1.4;
const CARD_W = 200;
const GAP = 20;
const TOTAL_ORIG = 5;
const SINGLE_SET_W = (CARD_W + GAP) * TOTAL_ORIG;

const WAVE_PRESETS = [
  [8, 14, 10, 18, 7, 12, 16, 9, 14, 6],
  [12, 6, 18, 10, 14, 8, 16, 11, 7, 13],
  [16, 9, 13, 18, 7, 11, 15, 8, 12, 17],
  [10, 17, 8, 13, 18, 6, 14, 11, 16, 9],
  [14, 8, 18, 11, 6, 15, 9, 13, 17, 7],
];

const CAROUSEL_ITEMS = [
  {
    rawClass: 'fp-bg-raw-1',
    aiClass: 'fp-bg-ai-1',
    beforeVideo: 'before1.mp4',
    afterVideo: 'after1.mp4',
    emoji: '🎙️',
    dur: '1:24:08',
    caption: <>This changes <span className="fp-caption-highlight">everything</span> about content</>,
    views: '2M',
    plat: 'play',
  },
  {
    rawClass: 'fp-bg-raw-2',
    aiClass: 'fp-bg-ai-2',
    beforeVideo: 'before2.mp4',
    afterVideo: 'after2.mp4',
    emoji: '🎤',
    dur: '2:05:33',
    caption: <>The <span className="fp-caption-highlight">secret</span> nobody talks about</>,
    views: '3M',
    plat: 'tiktok',
  },
  {
    rawClass: 'fp-bg-raw-3',
    aiClass: 'fp-bg-ai-3',
    beforeVideo: 'before3.mp4',
    afterVideo: 'after3.mp4',
    emoji: '🧠',
    dur: '45:12',
    caption: <>I studied <span className="fp-caption-highlight">10,000 hours</span> so you don&apos;t have to</>,
    views: '1M',
    plat: 'youtube',
  },
  {
    rawClass: 'fp-bg-raw-4',
    aiClass: 'fp-bg-ai-4',
    beforeVideo: 'before4.mp4',
    afterVideo: 'after4.mp4',
    emoji: '💡',
    dur: '1:12:44',
    caption: <>Do <span className="fp-caption-highlight">this</span> every morning for 30 days</>,
    views: '4M',
    plat: 'instagram',
    igGradId: 'fp-ig-a',
  },
  {
    rawClass: 'fp-bg-raw-5',
    aiClass: 'fp-bg-ai-5',
    beforeVideo: 'before5.mp4',
    afterVideo: 'after5.mp4',
    emoji: '🎬',
    dur: '3:20:05',
    caption: <>Why <span className="fp-caption-highlight">99%</span> of creators quit too early</>,
    views: '32M',
    plat: 'play',
  },
];

function CarouselCard({ item, waveHeights, lazyVideos }) {
  const pub = process.env.PUBLIC_URL || '';
  const beforeSrc = item.beforeVideo ? `${pub}/${item.beforeVideo}` : null;
  const afterSrc = item.afterVideo ? `${pub}/${item.afterVideo}` : null;

  return (
    <div className="fp-carousel-card">
      <div className="fp-before-layer">
        <div className={`fp-card-visual ${item.rawClass}`}>
          {beforeSrc ? (
            <video
              className="fp-carousel-card-video"
              {...(lazyVideos
                ? { 'data-src': beforeSrc }
                : { src: beforeSrc, autoPlay: true })}
              preload="none"
              loop
              muted
              playsInline
              aria-hidden
            />
          ) : (
            <span className="fp-card-person fp-card-person-raw">{item.emoji}</span>
          )}
          {!beforeSrc && (
            <>
              <div className="fp-raw-indicator">
                {waveHeights.map((h, i) => (
                  <div key={i} className="fp-wave-bar" style={{ height: `${h}px` }} />
                ))}
              </div>
              <div className="fp-progress-bar-raw" />
            </>
          )}
        </div>
      </div>
      <div className="fp-after-layer">
        <div className="fp-after-strip">
          <div className="fp-after-strip-inner">
            <div className={`fp-card-visual ${item.aiClass}`}>
              {afterSrc ? (
                <video
                  className="fp-carousel-card-video"
                  {...(lazyVideos
                    ? { 'data-src': afterSrc }
                    : { src: afterSrc, autoPlay: true })}
                  preload="none"
                  loop
                  muted
                  playsInline
                  aria-hidden
                />
              ) : (
                <span className="fp-card-person">{item.emoji}</span>
              )}
              <div className="fp-views-badge">
                <span className="fp-vn">{item.views}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevealCarouselBlock() {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);
  const gateRef = useRef(null);
  const offsetRef = useRef(-SINGLE_SET_W);
  const pausedRef = useRef(false);
  const sectionInViewRef = useRef(true);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const rafRef = useRef(null);

  const updateReveal = useCallback(() => {
    const gateEl = gateRef.current;
    const gateRect = gateEl?.getBoundingClientRect();
    const gateX = gateRect
      ? gateRect.left + gateRect.width / 2
      : document.documentElement.clientWidth / 2;
    const cards = trackRef.current?.querySelectorAll('.fp-carousel-card') ?? [];
    cards.forEach((card) => {
      const strip = card.querySelector('.fp-after-strip');
      if (!strip) return;
      const rect = card.getBoundingClientRect();
      const revealAmount = rect.right - gateX;
      const visible = Math.max(0, Math.min(revealAmount, rect.width));
      strip.style.width = `${visible}px`;
    });
  }, []);

  const hydrateLazyCarouselVideos = useCallback(() => {
    const track = trackRef.current;
    const gateEl = gateRef.current;
    if (!track || !gateEl) return;
    const gateRect = gateEl.getBoundingClientRect();
    const gateX = gateRect.left + gateRect.width / 2;
    const nearThreshold = CARD_W * 3;

    track.querySelectorAll('video[data-src]').forEach((video) => {
      if (video.getAttribute('src')) return;
      const url = video.dataset.src;
      if (!url) return;
      const card = video.closest('.fp-carousel-card');
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const distFromGate = Math.abs(cx - gateX);
      if (distFromGate < nearThreshold) {
        video.src = url;
        video.play().catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const onMouseDown = (e) => {
      isDraggingRef.current = true;
      dragStartXRef.current = e.clientX;
      dragStartOffsetRef.current = offsetRef.current;
      pausedRef.current = true;
    };
    const onMouseMove = (e) => {
      if (!isDraggingRef.current) return;
      const next = dragStartOffsetRef.current + (e.clientX - dragStartXRef.current);
      offsetRef.current = Math.max(-SINGLE_SET_W, Math.min(0, next));
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      pausedRef.current = false;
    };

    track.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    const animate = () => {
      if (!pausedRef.current && sectionInViewRef.current) {
        offsetRef.current += SPEED;
        if (offsetRef.current >= 0) {
          offsetRef.current -= SINGLE_SET_W;
        }
      }
      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${offsetRef.current}px)`;
      }
      updateReveal();
      hydrateLazyCarouselVideos();
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);

    const onResize = () => {
      updateReveal();
    };
    window.addEventListener('resize', onResize);

    return () => {
      track.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [updateReveal, hydrateLazyCarouselVideos]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const videos = section.querySelectorAll('video');
        videos.forEach((v) => {
          if (entry.isIntersecting) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
        sectionInViewRef.current = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videos = entry.target.querySelectorAll('video');
          videos.forEach((v) => {
            if (entry.isIntersecting) {
              v.play().catch(() => {});
            } else {
              v.pause();
            }
          });
        });
      },
      { threshold: 0.1 }
    );

    track.querySelectorAll('.fp-carousel-card').forEach((card) => {
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const cards = [...CAROUSEL_ITEMS, ...CAROUSEL_ITEMS, ...CAROUSEL_ITEMS];

  return (
    <section ref={sectionRef} className="fp-block fp-reveal-block" aria-label="Raw video to AI clips">
      <div className="fp-reveal-heading">
        <h2>
          Raw video → <span>Viral clips</span>
        </h2>
        <p>No Manual Editing. Just Clips That Gets Watched</p>
      </div>

      <div className="fp-slider-section">
        <div className="fp-reveal-gate" ref={gateRef} aria-hidden>
          <div className="fp-gate-label">✦ AI Transform</div>
          <div className="fp-legend">
            <div className="fp-legend-item">
              <div className="fp-legend-dot-before" />
              Original
            </div>
            <div className="fp-legend-sep">|</div>
            <div className="fp-legend-item">
              <div className="fp-legend-dot-after" />
              AI Clip
            </div>
          </div>
        </div>

        <div className="fp-carousel-track" ref={trackRef}>
          {cards.map((item, idx) => (
            <CarouselCard
              key={`c-${idx}`}
              item={item}
              waveHeights={WAVE_PRESETS[idx % TOTAL_ORIG]}
              lazyVideos={idx >= TOTAL_ORIG}
            />
          ))}
        </div>
      </div>

      <div className="fp-bottom-labels">
        <span className="fp-lbl-before">Original Video</span>
        <span className="fp-lbl-after">AI Clip</span>
      </div>
    </section>
  );
}

function FeatureViewsBlock() {
  return (
    <section className="fp-block fp-views-section" aria-label="Shorts that get views">
      <div className="fp-views-heading">
        <h2>
          Create Shorts That Get <span className="fp-eye">👁️</span> Views
        </h2>
      </div>
      <p className="fp-views-subtitle">
        Reels made with our tool are <span className="fp-stat">&quot;12x more&quot;</span> likely to get{' '}
        <span className="fp-shared">Shared</span>, <span className="fp-saved">Saved</span>, and{' '}
        <span className="fp-seen">Seen.</span>
      </p>

      <div className="fp-cards-stage">
        <div className="fp-glow-left" />
        <div className="fp-glow-right" />

        <div className="fp-phone-card fp-phone-left">
          <div className="fp-bg-left">
            <div className="fp-bokeh">
              <div className="fp-bokeh-dot" style={{ width: 40, height: 40, background: '#ff66c4', top: '15%', left: '20%' }} />
              <div className="fp-bokeh-dot" style={{ width: 24, height: 24, background: '#5170ff', top: '25%', left: '60%' }} />
              <div className="fp-bokeh-dot" style={{ width: 32, height: 32, background: '#ffb800', top: '10%', left: '75%' }} />
              <div className="fp-bokeh-dot" style={{ width: 18, height: 18, background: '#ff66c4', top: '40%', left: '10%' }} />
              <div className="fp-bokeh-dot" style={{ width: 28, height: 28, background: '#5170ff', top: '5%', left: '45%' }} />
              <div className="fp-bokeh-dot" style={{ width: 16, height: 16, background: '#fff', top: '30%', left: '80%', opacity: 0.4 }} />
              <div className="fp-bokeh-dot" style={{ width: 20, height: 20, background: '#ff2d78', top: '20%', left: '35%' }} />
            </div>
          </div>
          <div className="fp-video-placeholder">
            <video
              className="fp-phone-video"
              src={`${process.env.PUBLIC_URL || ''}/shorts1.mp4`}
              autoPlay
              loop
              muted
              playsInline
              aria-label="Example short — college"
            />
          </div>
          <div className="fp-card-caption">
            <span className="fp-caption-word fp-caption-pink">college.</span>
          </div>
          <div className="fp-views-badge-phone">
            <span className="fp-play-icon">▶</span> 273K
          </div>
        </div>

        <div className="fp-phone-card fp-phone-right">
          <div className="fp-bg-right">
            <div className="fp-bokeh">
              <div className="fp-bokeh-dot" style={{ width: 60, height: 60, background: '#2a7a20', top: '5%', left: '10%', filter: 'blur(8px)', opacity: 0.5 }} />
              <div className="fp-bokeh-dot" style={{ width: 50, height: 50, background: '#4a9a30', top: '15%', right: '10%', filter: 'blur(6px)', opacity: 0.4 }} />
              <div className="fp-bokeh-dot" style={{ width: 80, height: 80, background: '#1a5a10', top: '30%', left: '30%', filter: 'blur(12px)', opacity: 0.3 }} />
            </div>
          </div>
          <div className="fp-video-placeholder">
            <video
              className="fp-phone-video"
              src={`${process.env.PUBLIC_URL || ''}/shorts2.mp4`}
              autoPlay
              loop
              muted
              playsInline
              aria-label="Example short — day thank"
            />
          </div>
          <div className="fp-card-caption fp-card-caption-gap">
            <span className="fp-caption-word fp-caption-plain">day.</span>
            <span className="fp-caption-word fp-caption-blue">Thank</span>
          </div>
          <div className="fp-views-badge-phone">
            <span className="fp-play-icon">▶</span> 3.5M
          </div>
        </div>
      </div>

      <div className="fp-stats-row">
        <div className="fp-stat-item">
          <div className="fp-stat-num">12x</div>
          <div className="fp-stat-label">More likely to be shared</div>
        </div>
        <div className="fp-stat-sep" />
        <div className="fp-stat-item">
          <div className="fp-stat-num">6 clips</div>
          <div className="fp-stat-label">From one long video</div>
        </div>
        <div className="fp-stat-sep" />
        <div className="fp-stat-item">
          <div className="fp-stat-num">35+</div>
          <div className="fp-stat-label">Languages supported</div>
        </div>
      </div>
    </section>
  );
}

function BeyondClippingBlock() {
  return (
    <section className="fp-block fp-beyond-section" aria-label="Just upload. We Handle">
      <div className="fp-beyond-heading">
        <h2>
          Just upload. <span>We Handle</span>
        </h2>
        <p>Auto captions, silence removal, reframing, and more. All inside Makereels.</p>
      </div>

      <div className="fp-beyond-grid">
        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_silencetrim.png`}
              alt="Silence trim: remove dead air from clips"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Auto silence trimmer</div>
            <p className="fp-card-desc">
               Every filler word, pause, and silence stripped out so your clips. No excuse for viewers to drop off.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_reframe.png`}
              alt="Auto reframing: horizontal to vertical crop"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Auto reframing</div>
            <p className="fp-card-desc">
              Ready to post every time. Face tracking crops your horizontal video to vertical automatically.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_language.png`}
              alt="Multi-language captions in the app"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">35+ Languages</div>
            <p className="fp-card-desc">
              Auto-generated captions in over 35 languages. Styled, synced, and ready to post without any manual editing.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_style.png`}
              alt="Caption styles and presets for polished clips"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Preset Styles</div>
            <p className="fp-card-desc">
              Pick a style. Your video instantly looks like top creators. Clean captions, bold highlights, ready to post.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_hooks.png`}
              alt="AI hooks: strong openers and attention-grabbing moments"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">AI Hooks</div>
            <p className="fp-card-desc">
              We find the moments people actually watch. Your clips start strong and keep attention till the end.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_sfx.png`}
              alt="Sound effects and transitions for more energetic clips"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Sound Effects</div>
            <p className="fp-card-desc">
              Add energy without editing anything. Smooth transitions, subtle sounds — makes your videos feel alive.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_streak.png`}
              alt="Creator streak: daily posting reminders to stay consistent"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Creator Streak</div>
            <p className="fp-card-desc">
              Stay consistent with daily posting reminders. Know when it&apos;s time to publish and keep your streak alive.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_scheduling.png`}
              alt="Smart scheduling across YouTube, Instagram, Facebook, LinkedIn, and X"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">Smart Scheduling</div>
            <p className="fp-card-desc">
              Schedule content to YouTube, Instagram, Facebook, LinkedIn, X, and more from one simple calendar.
            </p>
          </div>
        </div>

        <div className="fp-feat-card">
          <div className="fp-feat-card-visual fp-feat-lang-visual">
            <img
              src={`${process.env.PUBLIC_URL || ''}/feature_1tappublish.png`}
              alt="One-tap publish to every connected social account"
              className="fp-feat-lang-img"
            />
          </div>
          <div className="fp-card-footer">
            <div className="fp-card-title">1-Tap Publish</div>
            <p className="fp-card-desc">
              Publish to every connected social account with your preferred captions, timing, and settings in one tap.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function StepsBlock() {
  return (
    <section className="fp-block fp-steps-section" aria-label="How it works">
      <div className="fp-steps-heading">
        <h2>
          Show me <span>HOW NOW!</span>
        </h2>
      </div>

      <div className="fp-steps-wrap">
        <div className="fp-steps-grid">
          <div className="fp-step">
            <div className="fp-card-mock fp-step-card-photo">
              <div className="fp-step-photo-visual">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/step1.png`}
                  alt="Step 1: upload or paste a link"
                  className="fp-step-photo-img"
                />
                <div className="fp-step-photo-fade" aria-hidden />
              </div>
            </div>
            <div className="fp-step-badge">Step 1</div>
            <div className="fp-step-title">INPUT RAW VIDEO</div>
            <p className="fp-step-desc">Upload a video or paste a YouTube link for the Raw footage.</p>
          </div>

          <div className="fp-step">
            <div className="fp-card-mock fp-step-card-photo">
              <div className="fp-step-photo-visual">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/step2.png`}
                  alt="Step 2: generate shorts with one tap"
                  className="fp-step-photo-img"
                />
                <div className="fp-step-photo-fade" aria-hidden />
              </div>
            </div>
            <div className="fp-step-badge">Step 2</div>
            <div className="fp-step-title">1-TAP</div>
            <p className="fp-step-desc">
               Pick a style preset and click on <b>⚡︎Generate Shorts</b>. We will handle the rest.
            </p>
          </div>

          <div className="fp-step">
            <div className="fp-card-mock fp-step-card-photo">
              <div className="fp-step-photo-visual">
                <img
                  src={`${process.env.PUBLIC_URL || ''}/step3.png`}
                  alt="Step 3: get your clips"
                  className="fp-step-photo-img"
                />
                <div className="fp-step-photo-fade" aria-hidden />
              </div>
            </div>
            <div className="fp-step-badge">Step 3</div>
            <div className="fp-step-title">GET CLIPS</div>
            <p className="fp-step-desc">Get up to 6 polished, ready-to-post potrait clips delivered to your Whatsaspp in original quality. No watermark, ever.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

const FEATURES_PAGE_BODY_CLASS = 'features-page-plain-bg';

const FeaturesPage = () => {
  useEffect(() => {
    document.body.classList.add(FEATURES_PAGE_BODY_CLASS);
    return () => document.body.classList.remove(FEATURES_PAGE_BODY_CLASS);
  }, []);

  useEffect(() => {
    trackFeaturesPageVisit();
  }, []);

  return (
    <div className="features-page">
      <div className="features-page-inner">
        <RevealCarouselBlock />
        <FeatureViewsBlock />
        <BeyondClippingBlock />
        <StepsBlock />
      </div>

      <footer className="footer">
        <div className="footer-content">
          <p className="footer-text">© 2026 Makereels.live. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
